// ReconnectOverlay — 再接続中の帯 (docs/_wip/reconnect-draw-lock-2026-10-12.md)。
//
// CanvasStage のステージ枠内・下端に出す。ブロック (描けない) は即時だが、帯の表示は
// RECONNECT_BAND_DELAY_MS 遅らせる (一瞬の切断でちらつかせない)。復帰したら即消す。
// ロック中にキャンバスをタップされたら (`tapKey` が増える) 遅延を待たず表示し、短く点滅する。
//
// a11y: 帯は role=status aria-live=polite (入ったときに 1 回読まれる)。「つながりました」は
// 常設の sr-only live region が 1 回読む。回数は aria-hidden。フォーカスは動かさない。

import { useEffect, useRef, useState } from 'react';

import { RECONNECT_BAND_DELAY_MS, useDelayedFlag } from '../hooks/useDelayedFlag.js';
import { useConnectionLock } from '../providers/ConnectionLockContext.js';

export const RECONNECTED_ANNOUNCE_MS = 3000;

export interface ReconnectOverlayProps {
  /** ロック中にキャンバスをタップされるたびに増える。変化で表示を強制 + 点滅。 */
  readonly tapKey: number;
}

const useDocumentHidden = (): boolean => {
  const [hidden, setHidden] = useState(() => typeof document !== 'undefined' && document.hidden);
  useEffect(() => {
    const onChange = (): void => setHidden(document.hidden);
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return hidden;
};

export const ReconnectOverlay = ({ tapKey }: ReconnectOverlayProps): React.ReactNode => {
  const { locked: lockedRaw, hydrating, attempt, maxAttempts } = useConnectionLock();
  // snapshot 適用待ちだけの間は帯を出さない (接続は復帰済み)。
  const locked = lockedRaw && hydrating !== true;
  const delayed = useDelayedFlag(locked, RECONNECT_BAND_DELAY_MS);
  const hidden = useDocumentHidden();

  // ロック開始時点の tapKey を覚え、それより増えていたら「タップされた」とみなす。
  const tapBaseRef = useRef(tapKey);
  useEffect(() => {
    if (!locked) tapBaseRef.current = tapKey;
  }, [locked, tapKey]);
  const tapped = locked && tapKey !== tapBaseRef.current;
  const visible = locked && (delayed || tapped);

  // 「つながりました」: 帯が出ていた後に復帰したときだけ、sr-only で 1 回読ませる。
  const wasVisibleRef = useRef(false);
  const [announce, setAnnounce] = useState('');
  useEffect(() => {
    if (visible) {
      wasVisibleRef.current = true;
      setAnnounce('');
      return;
    }
    if (!locked && wasVisibleRef.current) {
      wasVisibleRef.current = false;
      setAnnounce('つながりました');
      const timer = setTimeout(() => setAnnounce(''), RECONNECTED_ANNOUNCE_MS);
      return () => clearTimeout(timer);
    }
  }, [visible, locked]);

  return (
    <>
      <span role="status" aria-live="polite" className="sr-only" data-testid="reconnect-announce">
        {announce}
      </span>
      {visible && (
        <div
          role="status"
          aria-live="polite"
          data-oekaki-reconnect-band
          className="oekaki-reconnect-band pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-center justify-center gap-2 px-3 py-3 text-sm font-bold leading-snug text-[var(--color-canvas-frame)] sm:text-base"
        >
          {/* タップのたびに key が変わり、点滅アニメーションだけ再生し直す
              (帯本体は remount しない = live region を再読み上げさせない)。 */}
          {tapped && (
            <span
              key={tapKey}
              aria-hidden="true"
              data-oekaki-reconnect-flash
              className="oekaki-reconnect-band-flash absolute inset-0 bg-white/40 opacity-0"
            />
          )}
          <span className="relative min-w-0 text-center">
            つながりなおしているよ。ちょっと待ってね
          </span>
          {!hidden && attempt > 0 && (
            <span
              aria-hidden="true"
              className="relative ml-auto shrink-0 text-[10px] font-normal opacity-80"
            >
              ({attempt}/{maxAttempts})
            </span>
          )}
        </div>
      )}
    </>
  );
};
